import { Core } from "@faire/design-tokens";
import { useFormattedOfferedMaxTerms } from "@faire/retailer-visitor-shared/hooks/openWithFaire/useFormattedOfferedMaxTerms";
import { useGetFaireAcademyPageState } from "@faire/retailer-visitor-shared/hooks/openWithFaire/useGetFaireAcademyPageState";
import {
  usePopSignUp,
  SignUpType,
  SignUpElement,
} from "@faire/retailer-visitor-shared/lib/popSignUp";
import { useGuideStore } from "@faire/retailer-visitor-shared/views/open-with-faire/academy/guidePages/useGuideStore";
import { FaireAcademyChapter } from "@faire/retailer-visitor-shared/views/open-with-faire/academy/types";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Path } from "@faire/web--source/common/Path";
import { Button } from "@faire/web--source/slate/Button";
import { Check } from "@faire/web--source/slate/icons/foundation/Check";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackOpenWithFaireFaireAcademyLoggedOutSignUpNudgeSignInClickClick } from "@faire/web-api--source/events/openWithFaire/click/faireAcademyLoggedOutSignUpNudgeSignInClick";
import { trackOpenWithFaireFaireAcademyLoggedOutSignUpNudgeSignUpClickClick } from "@faire/web-api--source/events/openWithFaire/click/faireAcademyLoggedOutSignUpNudgeSignUpClick";
import { trackOpenWithFaireFaireAcademyLoggedOutSignUpNudgeViewView } from "@faire/web-api--source/events/openWithFaire/view/faireAcademyLoggedOutSignUpNudgeView";
import React from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";

export const SignUpPrompt: React.FC<{
  isNextChapterPreview?: boolean;
  chapter: FaireAcademyChapter;
}> = ({ isNextChapterPreview, chapter }) => {
  const [{ guide }] = useGuideStore(["guide"]);
  const { pageState } = useGetFaireAcademyPageState();
  const { popSignUp } = usePopSignUp();
  const signUpBenefits = useGetSignUpBenefits();
  const location = useLocation();
  const currentPath = new Path(location);
  const currentUrl = currentPath.toString();
  const loginUrl = currentPath
    .append({ signIn: "1", redirectTo: currentUrl })
    .toString();

  const handleSignUp = () => {
    popSignUp(
      SignUpType.GENERIC,
      () => {},
      {
        element: SignUpElement.NUDGE,
        sourceId: `faire_academy_${pageState.toLowerCase()}`,
      },
      { redirectTo: currentUrl }
    );
    trackOpenWithFaireFaireAcademyLoggedOutSignUpNudgeSignUpClickClick(
      pageState,
      guide.id,
      chapter.id
    );
  };

  const handleSignIn = () => {
    trackOpenWithFaireFaireAcademyLoggedOutSignUpNudgeSignInClickClick(
      pageState,
      guide.id,
      chapter.id
    );
  };

  const ref = useDoActionWhenVisible(() => {
    trackOpenWithFaireFaireAcademyLoggedOutSignUpNudgeViewView(
      pageState,
      guide.id,
      chapter.id
    );
  });

  return (
    <BlueColumn align="center" ref={ref}>
      <FixedWithColumn>
        <Typography variant="displaySSerifRegular" align="center">
          {isNextChapterPreview ? (
            <StrictLocalMsg
              defaultMessage="Jump to the next chapter and access all of Faire"
              description={{
                text: "Go to the next chapter after signing in or signing up to Faire",
                img: "https://cdn.faire.com/fastly/4f36816a91d4859e8d77861f1c845c57c1477cab97e6e3b6046cbfaf5f3b929e.png",
              }}
            />
          ) : (
            <StrictLocalMsg
              defaultMessage="Read this chapter and access all of Faire"
              description={{
                text: "Access this Faire Academy chapter after signing in or signing up to Faire",
                img: "https://cdn.faire.com/fastly/7d2e1f92540679211a82544475328ce4282bd2fa5ba63feace7c3e372fadbb76.png",
              }}
            />
          )}
        </Typography>
        <Spacer height="3x" />
        <Column gap="2x">
          {signUpBenefits.map((signUpBenefit) => (
            <RowWithCheck key={signUpBenefit}>{signUpBenefit}</RowWithCheck>
          ))}
        </Column>
        <Spacer height="4x" />
        <Button onClick={handleSignUp}>
          <StrictLocalMsg
            defaultMessage="Sign up for free"
            description={{
              text: "Call to action to begin the sign up flow (CTA button text)",
              img: "https://cdn.faire.com/fastly/b4af2f5790b4e0752944ac056de2906b679cd6fd241b5101f29e827cc2523f2f.png",
            }}
          />
        </Button>
        <Spacer height="2x" />
        <Button variant="secondary" linkTo={loginUrl} onClick={handleSignIn}>
          <StrictLocalMsg
            defaultMessage="Sign in"
            description={{
              text: "Sign in to your account on Faire",
              img: "https://cdn.faire.com/fastly/4f36816a91d4859e8d77861f1c845c57c1477cab97e6e3b6046cbfaf5f3b929e.png",
            }}
          />
        </Button>
      </FixedWithColumn>
    </BlueColumn>
  );
};

const useGetSignUpBenefits = (): Array<string> => {
  const { strictLocalize } = useStrictLocalization();
  const formattedOfferedMaxTerms = useFormattedOfferedMaxTerms();
  return [
    strictLocalize(
      {
        defaultMessage: "Up to {maxTermsMoney} in 60-day payment terms",
        description: {
          text: "Benefit of joining Faire",
          img: "https://cdn.faire.com/fastly/4f36816a91d4859e8d77861f1c845c57c1477cab97e6e3b6046cbfaf5f3b929e.png",
        },
      },
      { maxTermsMoney: formattedOfferedMaxTerms }
    ),
    strictLocalize({
      defaultMessage: "Access to unique products for your store",
      description: {
        text: "Benefit of joining Faire",
        img: "https://cdn.faire.com/fastly/4f36816a91d4859e8d77861f1c845c57c1477cab97e6e3b6046cbfaf5f3b929e.png",
      },
    }),
    strictLocalize({
      defaultMessage: "Free returns on opening orders",
      description: {
        text: "Benefit of joining Faire",
        img: "https://cdn.faire.com/fastly/4f36816a91d4859e8d77861f1c845c57c1477cab97e6e3b6046cbfaf5f3b929e.png",
      },
    }),
    strictLocalize({
      defaultMessage: "One, easy-to-use site for your buying",
      description: {
        text: "Benefit of joining Faire",
        img: "https://cdn.faire.com/fastly/4f36816a91d4859e8d77861f1c845c57c1477cab97e6e3b6046cbfaf5f3b929e.png",
      },
    }),
  ];
};

const RowWithCheck: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return (
    <Row gap="1x" align="center">
      <Check />
      <Typography>{children}</Typography>
    </Row>
  );
};

const BlueColumn = styled(Column)`
  width: 100%;
  padding: ${getSpacing("6x")};
  background-color: ${Core.surface.info};

  ${responsiveMediaQueries.tabletAndBelow} {
    padding: ${getSpacing("3x", "2x")};
  }
`;

const FixedWithColumn = styled(Column)`
  max-width: 320px;
`;
